/**
 * Document Type Services
 *
 * Maps document types to their service classes for consistent API access patterns.
 */

import { AxiosResponse } from 'axios';
import { createApiClient } from 'services/apiClient';
import { ApiDocumentType, getDocumentTypeConfig } from './documentTypeRegistry';
import OrderService from './orders/services';
import OrderConfirmationService from './order-confirmations/services';
import ProductDescriptionService from './product-descriptions/services';
import IncomingQuoteService from './incoming-quotes/services';
import BackorderReportService from './backorder-reports/services';

/**
 * Common interface for document search operations
 */
export interface DocumentSearchService {
  search: (tenantId: string, criteria: any) => Promise<{ results: any[] }>;
  getStateDetail: (tenantId: string, documentId: string) => Promise<any>;
  deleteState: (tenantId: string, documentId: string, force?: boolean) => Promise<void>;
}

/**
 * Base search criteria shared across document types
 */
export interface BaseSearchCriteria {
  days_back?: number;
  limit?: number;
  parent_process_state_id?: string | null;
}

/**
 * Search criteria for orders
 */
export interface OrderSearchCriteria extends BaseSearchCriteria {
  order_status?: string | null;
  created_at_start?: string | null;
  created_at_end?: string | null;
  order_number?: string | null;
  order_state_id?: string | null;
  order_items_matched?: boolean | null;
  order_has_been_sent?: boolean | null;
  customer_number?: string | null;
  customer_name?: string | null;
  order_backend_id?: string | null;
  max_retry_reached?: boolean | null;
  has_errors?: boolean | null;
  hitl_required?: boolean | null;
  hitl_approved?: boolean | null;
  hitl_reason?: string | null;
  hitl_rule_id?: string | null;
  hitl_approved_by?: string | null;
  hitl_approved_at_start?: string | null;
  hitl_approved_at_end?: string | null;
}

/**
 * Search criteria for generic documents (order confirmations, product descriptions, etc.)
 */
export interface GenericDocumentSearchCriteria extends BaseSearchCriteria {
  document_status?: string | null;
  created_at_start?: string | null;
  created_at_end?: string | null;
  document_state_id?: string | null;
  has_been_sent?: boolean | null;
  max_retry_reached?: boolean | null;
  has_errors?: boolean | null;
  has_work_description?: boolean | null;
}

/**
 * Extended criteria for order confirmations
 */
export interface OrderConfirmationSearchCriteria extends GenericDocumentSearchCriteria {
  order_number?: string | null;
  offer_reference?: string | null;
}

/**
 * Extended criteria for product descriptions
 */
export interface ProductDescriptionSearchCriteria extends GenericDocumentSearchCriteria {
  articlenumber?: string | null;
  sku?: string | null;
  ean?: string | null;
}

/**
 * Extended criteria for incoming quotes
 */
export interface IncomingQuoteSearchCriteria extends GenericDocumentSearchCriteria {
  supplier_name?: string | null;
  quote_number?: string | null;
}

/**
 * Extended criteria for backorder reports
 */
export interface BackorderReportSearchCriteria extends GenericDocumentSearchCriteria {
  supplier_name?: string | null;
}

/**
 * Create default search criteria for a document type
 */
export function createDefaultSearchCriteria(
  type: ApiDocumentType,
  overrides?: Partial<any>
): any {
  const base = {
    days_back: 30,
    limit: 50,
    parent_process_state_id: null,
  };

  switch (type) {
    case 'order':
      return {
        ...base,
        order_status: null,
        created_at_start: null,
        created_at_end: null,
        order_number: null,
        order_state_id: null,
        order_items_matched: null,
        order_has_been_sent: null,
        customer_number: null,
        customer_name: null,
        order_backend_id: null,
        max_retry_reached: null,
        has_errors: null,
        hitl_required: null,
        hitl_approved: null,
        hitl_reason: null,
        hitl_rule_id: null,
        hitl_approved_by: null,
        hitl_approved_at_start: null,
        hitl_approved_at_end: null,
        ...overrides,
      } as OrderSearchCriteria;

    case 'order_confirmation':
      return {
        ...base,
        document_status: null,
        created_at_start: null,
        created_at_end: null,
        document_state_id: null,
        has_been_sent: null,
        order_number: null,
        offer_reference: null,
        max_retry_reached: null,
        has_errors: null,
        has_work_description: null,
        ...overrides,
      } as OrderConfirmationSearchCriteria;

    case 'product_description':
      return {
        ...base,
        document_status: null,
        created_at_start: null,
        created_at_end: null,
        document_state_id: null,
        has_been_sent: null,
        articlenumber: null,
        sku: null,
        ean: null,
        max_retry_reached: null,
        has_errors: null,
        has_work_description: null,
        ...overrides,
      } as ProductDescriptionSearchCriteria;

    case 'incoming_quote':
      return {
        ...base,
        document_status: null,
        created_at_start: null,
        created_at_end: null,
        document_state_id: null,
        has_been_sent: null,
        supplier_name: null,
        quote_number: null,
        max_retry_reached: null,
        has_errors: null,
        has_work_description: null,
        ...overrides,
      } as IncomingQuoteSearchCriteria;

    case 'backorder_report':
      return {
        ...base,
        document_status: null,
        created_at_start: null,
        created_at_end: null,
        document_state_id: null,
        has_been_sent: null,
        supplier_name: null,
        max_retry_reached: null,
        has_errors: null,
        has_work_description: null,
        ...overrides,
      } as BackorderReportSearchCriteria;

    default:
      return base;
  }
}

/**
 * Get the service class for a document type
 */
export function getDocumentService(type: ApiDocumentType): DocumentSearchService {
  switch (type) {
    case 'order':
      return {
        search: (tenantId, criteria) => OrderService.searchOrders(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => OrderService.getOrderStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => OrderService.deleteOrderState(tenantId, documentId, force),
      };

    case 'order_confirmation':
      return {
        search: (tenantId, criteria) => OrderConfirmationService.searchOrderConfirmations(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => OrderConfirmationService.getOrderConfirmationStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => OrderConfirmationService.deleteOrderConfirmationState(tenantId, documentId, force),
      };

    case 'product_description':
      return {
        search: (tenantId, criteria) => ProductDescriptionService.searchProductDescriptions(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => ProductDescriptionService.getProductDescriptionStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => ProductDescriptionService.deleteProductDescriptionState(tenantId, documentId, force),
      };

    case 'incoming_quote':
      return {
        search: (tenantId, criteria) => IncomingQuoteService.searchIncomingQuotes(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => IncomingQuoteService.getIncomingQuoteStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => IncomingQuoteService.deleteIncomingQuoteState(tenantId, documentId, force),
      };

    case 'backorder_report':
      return {
        search: (tenantId, criteria) => BackorderReportService.searchBackorderReports(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => BackorderReportService.getBackorderReportStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => BackorderReportService.deleteBackorderReportState(tenantId, documentId, force),
      };

    default:
      console.warn(`[DOCUMENT_TYPE_SERVICES] Unknown document type: ${type}, defaulting to order service`);
      return {
        search: (tenantId, criteria) => OrderService.searchOrders(tenantId, criteria),
        getStateDetail: (tenantId, documentId) => OrderService.getOrderStateDetail(tenantId, documentId),
        deleteState: (tenantId, documentId, force) => OrderService.deleteOrderState(tenantId, documentId, force),
      };
  }
}

/**
 * Search documents using the appropriate service
 */
export async function searchDocuments(
  type: ApiDocumentType,
  tenantId: string,
  criteria: any
): Promise<any[]> {
  const service = getDocumentService(type);
  const response = await service.search(tenantId, criteria);
  return response.results || [];
}

/**
 * Get document state detail using the appropriate service
 */
export async function getDocumentStateDetail(
  type: ApiDocumentType,
  tenantId: string,
  documentId: string
): Promise<any> {
  const service = getDocumentService(type);
  return service.getStateDetail(tenantId, documentId);
}

/**
 * Delete a document state using the appropriate service
 */
export async function deleteDocumentState(
  type: ApiDocumentType,
  tenantId: string,
  documentId: string,
  force: boolean = false
): Promise<void> {
  const service = getDocumentService(type);
  return service.deleteState(tenantId, documentId, force);
}


const unparkApiClient = createApiClient('UNPARK');

/**
 * Un-park documents whose send retries were exhausted, so the scheduled sweep
 * picks them up again.
 *
 * One implementation for every type with a route: the URL segment is the
 * registry's own `basePath`, which matches the API path exactly for all five
 * document types. A new type needs no code here — only an entry in the
 * caller's allowlist once its route exists.
 *
 * Resets the retry counters; does **not** send. The next sweep sends, which is
 * why every caller's copy is future tense.
 *
 * Always HTTP 200 on a reachable route: branch on `unparked_count` /
 * `failed_count`, never on `status`. A transport failure rejects instead, and
 * the caller turns that group's ids into failures rather than losing the other
 * types' results.
 */
export async function bulkUnparkDocuments(
  type: ApiDocumentType,
  tenantId: string,
  ids: string[]
): Promise<any> {
  if (!ids || ids.length === 0) {
    throw new Error('No documents selected to un-park');
  }
  const basePath = getDocumentTypeConfig(type).route.basePath;
  const path = `/business-documents/${basePath}/state/${tenantId}/bulk-unpark`;

  console.log('♻️ [UNPARK] Un-parking documents:', { type, path, count: ids.length });

  const response: AxiosResponse<any> = await unparkApiClient.post(path, { ids });

  console.log('✅ [UNPARK] Result:', {
    type,
    unparked_count: response.data?.unparked_count,
    failed_count: response.data?.failed_count,
  });

  return response.data;
}
